import { ROUTES } from '../../../constants/routes';
import type {
  LandingCallout,
  LandingContactInfoBlock,
  LandingCaseStudyMetric,
  LandingBehaviorScaleLevel,
  LandingDashboardFeature,
  LandingFeature,
  LandingFooterColumn,
  LandingNavItem,
  LandingPricingTier,
  LandingStat,
  LandingTrait,
  LandingValueProp,
  LandingVideoContent,
} from '../types';

/** Section anchor ids — used by nav/footer links until dedicated routes exist. */
export const LANDING_SECTION_IDS = {
  hero: 'hero',
  stats: 'stats',
  problem: 'problem',
  science: 'science',
  video: 'video',
  valueProps: 'value-props',
  features: 'features',
  dashboard: 'dashboard',
  caseStudy: 'case-study',
  pricing: 'pricing',
  cta: 'cta',
  contact: 'contact',
} as const;

/** Figma Color/Primary/Main P — default heading emphasis. */
export const LANDING_EMPHASIS_PRIMARY_COLOR = '#ED2991';

/** Figma Color/Secondary/Main S — orange heading emphasis. */
export const LANDING_EMPHASIS_SECONDARY_COLOR = '#F47521';

export const landingNavItems: LandingNavItem[] = [
  { label: 'About Us', href: `#${LANDING_SECTION_IDS.hero}` },
  { label: 'Our Science', href: `#${LANDING_SECTION_IDS.problem}` },
  { label: 'How It works', href: `#${LANDING_SECTION_IDS.science}` },
  { label: 'For Shelters', href: `#${LANDING_SECTION_IDS.features}` },
  { label: 'Analytics', href: `#${LANDING_SECTION_IDS.dashboard}` },
  { label: 'Pricing', href: `#${LANDING_SECTION_IDS.pricing}` },
  { label: 'Blogs', href: `#${LANDING_SECTION_IDS.caseStudy}` },
  { label: 'Contact', href: `#${LANDING_SECTION_IDS.contact}` },
];

export const landingGetStartedCta = {
  label: 'Get started for free',
  href: `#${LANDING_SECTION_IDS.contact}`,
} as const;

export const landingContactSalesCta = {
  label: 'Contact sales',
  href: `#${LANDING_SECTION_IDS.contact}`,
} as const;

export const landingNavCtas = {
  logIn: { label: 'Log in', href: ROUTES.auth },
  primary: { label: 'Try for free', href: `#${LANDING_SECTION_IDS.contact}` },
} as const;

export const landingScheduleDemoCta = {
  label: 'Schedule a demo',
  href: `#${LANDING_SECTION_IDS.contact}`,
} as const;

export const landingHero = {
  eyebrow: 'Behavior-based adoption · Nonprofit-founded',
  titleParts: [
    'Stop losing ',
    { emphasis: 'good matches' },
    ' to bad data',
  ] as const,
  description:
    'Friendr™ is a web based solution that gives animal shelters the tools to match adopters and pets based on behavioral factors, which are scientifically linked to adopter satisfaction',
  primaryCta: landingScheduleDemoCta,
  secondaryCta: {
    label: 'See how it works',
    href: `#${LANDING_SECTION_IDS.video}`,
  },
  visual: {
    src: '/brand/hero-pet-matches.png',
    alt: 'Friendr pet matches screen showing compatibility-ranked dog profiles',
  },
};

export const landingStats: LandingStat[] = [
  {
    value: '37%',
    label:
      'Increase in adoptions at Friends For Life in the first 6 months of implementation.',
  },
  { value: '25%', label: 'More senior pets adopted' },
  { value: '20%', label: 'Reduction on dog failure adoptions' },
];

export const landingStatsTagline =
  'Better data, better matches, more lives saved.';

export const landingProblem = {
  eyebrow: 'The challenge',
  titleParts: [
    'Most shelters are still matching people to animal ',
    { emphasis: 'photos' },
  ] as const,
  paragraphs: [
    'The data is stark: roughly one in five adopted pets is returned in the first year. Not because people stop caring but because the match was never right.',
    'Existing adoption tools showcase animals by superficial characteristics: breed, age, and weight. What they miss is the behavioral information that actually predicts whether an adoption sticks: energy level, affection style, how an animal behaves around kids, cats, or strangers. That data lives in your shelter — Friendr puts it to work.',
    'The science of "choice paralysis" shows that giving people too many undifferentiated options like scrolling pages of cute animals leads to poor decisions.. Friendr replaces browsing with a guided, structured experience that narrows the field based on what actually matters',
  ],
  callouts: [
    {
      title:
        'Nearly half a million shelter animals are returned every year in the US',
      description:
        'Returns increase intake rates and reduce future shelter adoptions. Behavioral mismatch is the #1 cause of failed adoptions, but this is solvable with better data.',
    },
    {
      title: 'Appearance-driven browsing drives impulse, not fit',
      description:
        'When looks drive the decision, fit takes a back seat. Adopters pick the animal they love in the moment. Friendr shifts the basis of the match from "cute" to "compatible."',
    },
    {
      title: 'Your staff knowledge about the animals is underutilized',
      description:
        "Fosters, intake staff, and teams have behaviour information about each animal. Friendr creates the pipeline that gets that knowledge to adopters at the moment they're deciding.",
    },
  ] satisfies LandingCallout[],
};

/** Figma Color/Other/Surface 1 — methodology section background. */
export const LANDING_SURFACE_1_COLOR = '#34254A';

/** Figma final CTA band background. */
export const LANDING_FINAL_CTA_BG = '#2C1F3C';

export const landingScience = {
  eyebrow: 'Our methodology- MAKE',
  titleParts: [
    'Decades of shelter experience. ',
    { emphasis: 'Backed by peer-reviewed science' },
  ] as const,
  quote:
    "It isn't just animals who benefit from having agency in their choices — adopters benefit too.",
  paragraphs: [
    'Friendr draws on research by Rosenfeld, Hausenat, and Thomas at Stanford University and the University of New Mexico, applying the concept of disintermediation: removing friction between a person and the right outcome by replacing guesswork with structured, meaningful data.',
    "Combined with Melissa Taylor's decades of hands-on behavior work at Friends For Life, the framework translates cutting-edge behavioral science into a protocol any shelter team can implement without requiring a behavior team.",
    'Every animal in Friendr is scored across 7 behavioral attributes by the intake team and foster carers. The scoring is consistent cross-shelter: a 3/5 for "good with dogs" means the same thing at every shelter on the platform. That shared language is what makes the matches trustworthy.',
  ],
  author: {
    name: 'Melissa Taylor, CBCC-KA, CSB-C',
    details:
      'Behavior & Training Manager, Friends For Life Animal Shelter · Creator of the Friendr behavioral framework · Houston, Texas',
  },
  attributesHeading: 'The 7 attributes empowering every match',
  attributes: [
    { label: 'Behavior with dogs' },
    { label: 'Behavior with cats' },
    { label: 'Behavior with kids' },
    { label: 'Energy level' },
    { label: 'Affection style' },
    { label: 'New people comfort' },
    { label: 'House manners' },
  ] satisfies LandingTrait[],
  scaleHeading: 'Each behavior attribute is scored on a scale of 1 – 5',
  scaleLevels: [
    { level: '1', label: 'Not compatible', value: 20 },
    { level: '2', label: 'Limited experience', value: 38 },
    { level: '3', label: 'With structured intro', value: 56 },
    { level: '4', label: 'Generally friendly', value: 74 },
    { level: '5', label: 'Loves everyone', value: 100 },
  ] satisfies LandingBehaviorScaleLevel[],
  scaleFooter:
    'Scores are collected by intake staff, foster carers, and behavior specialists using your existing shelter system. Friendr provides a guided process to bring that data in — your shelter system stays the source of truth.',
};

/** Figma card surface on the pink "How it works" band. */
export const LANDING_VALUE_PROP_CARD_COLOR = '#2D2438';

export const landingValueProps = {
  eyebrow: 'How it works',
  title: 'From data entry to forever home',
  description:
    'Friendr fits into your existing workflow. No duplicate data entry. Just a behavior-first layer on top of what you already do.',
  items: [
    {
      icon: 'clipboard',
      title: 'Your team scores animals in your shelter system',
      description:
        "Friendr's easy to follow SOP and training modules helps your team rate each animal across all 7 attributes directly in your existing system.",
    },
    {
      icon: 'documentCheck',
      title: 'Adopter takes the quiz',
      description:
        'A structured questionnaire captures lifestyle, experience, household, and what the adopter actually needs in a companion, not just what looks cute online.',
    },
    {
      icon: 'home',
      title: 'Ranked matches delivered instantly',
      description:
        'Friendr compares profiles and generates a personalized match report with compatibility percentages. Adopters arrive knowing exactly who they want to meet.',
    },
  ] satisfies LandingValueProp[],
};

/** Figma feature grid section background. */
export const LANDING_FEATURE_GRID_BG = '#2D2424';

/** Figma Color/Secondary/Highlight s — feature card surface. */
export const LANDING_FEATURE_CARD_BG = '#FFD3B0';

export const landingFeatureGrid = {
  eyebrow: 'We see you, shelters',
  titleParts: [
    'Built by a ',
    { emphasis: 'Shelter', tone: 'secondary' },
    ' for Shelters',
  ] as const,
  description:
    "We understand limited budgets, stretched teams, and what it costs — emotionally and financially — when an adoption doesn't last. Friendr was designed in a working shelter — not a tech incubator. Every feature was built to fit real workflows, limited staff, and the operational reality of animal welfare organizations.",
  items: [
    {
      icon: 'link',
      title: 'Works with your existing shelter data',
      description:
        "We don't ask your team to start from scratch. Friendr includes a guided process to help shelters bring their existing animal data from their current system — Shelterluv, PetPoint, Chameleon, ShelterBuddy, and others — into Friendr efficiently. Your data, your way.",
    },
    {
      icon: 'trendingChart',
      title: 'Analytics that prove your ROI',
      description:
        "Preset dashboards show which animals are getting attention (and which aren't), quiz-to-application conversion, top behavioral attributes driving matches, and shelter compliance scores — without custom queries.",
    },
    {
      icon: 'academicCap',
      title: 'Staff training included in every plan',
      description:
        'Every tier includes the Friendr behavior framework, self-paced training modules, and pet evaluation templates. Your team gets consistent SOPs that produce reliable behavioral data from day one.',
    },
    {
      icon: 'users',
      title: 'Network effect across shelters',
      description:
        'Every shelter that joins Friendr enriches the platform. Cross-shelter demand data reveals what adopters in your community are actually looking for — intelligence no single shelter could generate alone.',
    },
    {
      icon: 'shield',
      title: 'No sign-up required for adopters',
      description:
        "Potential adopters take the quiz and get their matches: no account, no registration, no personal details required. If they want their results emailed, they can opt in. We don't collect, store, or manage adopter profiles on our platform. Less friction for them, less liability for you.",
    },
    {
      icon: 'heart',
      title: 'Shareable by design',
      description:
        'Friendr creates personalized matches adopters naturally share on social media, expanding animal visibility far beyond your shelter website.',
    },
  ] satisfies LandingFeature[],
};

export const landingDashboard = {
  eyebrow: 'Platform analytics',
  titleParts: [
    "See what's working. Act on what ",
    { emphasis: "isn't" },
  ] as const,
  description:
    "Friendr's built-in analytics dashboard gives shelter admins and platform managers the data they need to prove ROI, find overlooked animals, and understand what adopters in their community actually want.",
  screenshotSrc: '/brand/landing-analytics-dashboard-hi-res.png',
  screenshotAlt:
    'Friendr analytics dashboard showing quiz traffic, successful matches, and pet popularity metrics',
  features: [
    {
      icon: 'chart',
      title: 'Traffic & quiz funnel',
      description:
        'See where adopters come from — social, email, or direct — and how many move from page visit to quiz start to completion to application click. No vanity metrics.',
    },
    {
      icon: 'paw',
      title: 'Most and least attention animals',
      description:
        'Instantly see which animals are generating matches and which have gone weeks with zero quiz results — so your team can take targeted action before length of stay becomes a crisis.',
    },
    {
      icon: 'scale',
      title: 'Behavior gap analysis',
      description:
        'Compare what attributes adopters in your community are asking for versus what your current animals actually have. A living insight that helps you plan intake, foster focus, and community outreach.',
    },
    {
      icon: 'documentCheck',
      title: 'Profile compliance scoring',
      description:
        'Track what percentage of your animals have complete behavioral profiles. The 80% goal is your quality threshold — animals without complete data are flagged and excluded from the quiz automatically.',
    },
  ] satisfies LandingDashboardFeature[],
};

export const landingCaseStudy = {
  eyebrow: 'Success story',
  titleParts: [
    { emphasis: 'Roscoe' },
    " nearly didn't make it. Then he met Anthony.",
  ] as const,
  paragraphs: [
    'Every year, millions of animals enter shelters, but most adoption listings still lead with age, weight, and breed. The information that actually predicts a lasting match — personality, behavioral tendencies, affection style — gets left behind.',
    "When Roscoe arrived at a crowded shelter, his listing said almost nothing about who he was. After weeks with no match, he nearly didn't make it. Then Friends For Life stepped in, enrolled him in Friendr, and built his behavioral profile from scratch.",
    'The data revealed what no photo ever could: Roscoe scored a perfect 5 out of 5 for affection. A Velcro dog through and through.',
    "Anthony used the Friendr quiz knowing exactly what he wanted. Roscoe stood out, not for his looks, but because every attribute aligned with Anthony's life. The first time they met, it was obvious. That was his dog.",
  ],
  pullQuote:
    "Better data. Lasting matches. More lives saved. That's what Friendr is all about.",
  testimonialQuote:
    "From the moment I used Friendr, I knew what I needed. Roscoe's 5/5 affection score said everything. We met once and I knew. This was my dog.",
  attribution: 'Anthony & Roscoe',
  attributionDetail:
    'Matched via Friendr · Friends For Life Animal Shelter, Houston TX',
  metrics: [
    {
      value: '37%',
      label:
        'Adoption increase at Friends For Life within 6 months of Friendr launch',
    },
    {
      value: '1,300',
      label:
        "Additional animals projected to find homes per year if Houston's largest shelter adopts Friendr",
    },
    {
      value: '5/5',
      label:
        "Roscoe's affection score: the attribute that made him unmissable to the right adopter",
    },
    {
      value: '0',
      label:
        'Returns. Behavior-based matching means adopters go home with the right animal the first time.',
    },
  ] satisfies LandingCaseStudyMetric[],

  visual: {
    src: '/brand/roscoe-image.jpg',
    alt: 'Roscoe and Anthony',
  },
};

export const landingPricing = {
  eyebrow: 'Built for shelter teams',
  titleLine1: 'Everything your team needs.',
  titleLine2: "Nothing you don't.",
  description:
    'Friendr was designed in a working shelter — not a tech incubator. Every feature was built to fit real workflows, limited staff, and the operational reality of animal welfare organizations.',
  featuresHeading: 'Features',
  tiers: [
    {
      id: 'standard',
      name: 'Standard',
      price: '$179',
      priceSuffix: 'USD / Monthly',
      description: 'For adoption coordinators at small shelters and rescues',
      features: [
        'Friendr behavior framework access',
        'Self-paced staff training classes',
        'Pet evaluation templates',
        'Adopter matching web app',
        'Up to 30 animals in population',
        'Up to 20 staff users',
      ],
      cta: { label: 'Get started', href: `#${LANDING_SECTION_IDS.contact}` },
      ctaVariant: 'primary',
    },
    {
      id: 'plus',
      name: 'Plus',
      price: '$499',
      priceSuffix: 'USD / Monthly',
      description:
        'For mid-size shelters with multiple team roles and Shelter system integration',
      features: [
        'Everything in Standard',
        'Shelter system read integration (Shelterluv and more)',
        'Up to 100 animals in population',
        'Up to 50 staff users',
        'Full analytics dashboard',
        'Admin roles supported',
      ],
      cta: { label: 'Get started', href: `#${LANDING_SECTION_IDS.contact}` },
      ctaVariant: 'primary',
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '$999',
      priceSuffix: 'USD / Monthly',
      description:
        'For large and municipal shelters with high animal populations',
      features: [
        'Everything in Plus',
        'In-person onboarding training session',
        '200+ animals in population',
        'Up to 100 staff users',
        'Priority implementation support',
        'Built for shelters (450+ avg intake)',
      ],
      cta: { label: 'Get started', href: `#${LANDING_SECTION_IDS.contact}` },
      ctaVariant: 'primary',
    },
  ] satisfies LandingPricingTier[],
};

export const landingVideo: LandingVideoContent = {
  title: 'FRIENDR product testimonial',
  posterUrl: '/img/landing-video-prev.jpg',
  embedUrl:
    'https://drive.google.com/file/d/1-jjFxNa2iMxCB0khozTXEXNrck7epFLG/preview',
  placeholderMessage:
    'Video coming soon. Get started for free to see FRIENDR in action with your team.',
  closeLabel: 'Close',
};

export const landingFinalCta = {
  titleParts: ['Ready to find more ', { emphasis: 'Roscoes?' }] as const,
  description:
    "Schedule a 30-minute demo with the Friendr team. We'll show you the platform, check your shelter system compatibility, and find the right plan for your shelter.",
  primaryCta: landingScheduleDemoCta,
  secondaryCta: {
    label: 'Try the adopter quiz',
    href: ROUTES.adoptShelterQuizRoot + '/friends-for-life',
  },
};

export const landingContactFormFields = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Work email',
  shelterName: 'Shelter or organization name',
  annualIntake: 'Annual animal intake',
  shelterSystem: 'Current shelter system',
  message: 'What are you hoping Friendr can help with?',
  placeholders: {
    firstName: 'Your first name',
    lastName: 'Your last name',
    email: 'you@email.com',
    shelterName: 'Shelter name',
    annualIntake: '200',
    shelterSystem: 'Chameleon',
    message: 'Tell us about yourself',
  },
  successTitle: 'Message sent',
} as const;

export const landingContactAnnualIntakeOptions = [
  'Under 100',
  '100–200',
  '200–500',
  '500–1,000',
  '1,000+',
] as const;

export const landingContactShelterSystemOptions = [
  'Chameleon',
  'Shelterluv',
  'PetPoint',
  'ShelterBuddy',
  'Other',
  'None / not sure',
] as const;

export const landingContact = {
  eyebrow: 'Get in touch',
  titleParts: ["Let's get your shelter ", { emphasis: 'started' }] as const,
  submitLabel: 'Send message',
  successMessage:
    'Thanks — we received your message. A member of our team will be in touch shortly.',
  infoBlocks: [
    {
      title: 'Who we are',
      body: "Friendr was created inside Friends For Life Animal Shelter — a progressive animal welfare organization of thought leaders in Houston, Texas. We built what we needed, and now we're sharing it with the entire shelter community.",
    },
    {
      title: 'Email us',
      link: {
        label: 'info@myfriendr.com',
        href: 'mailto:info@myfriendr.com',
      },
    },
    {
      title: 'Address',
      address: '107 East 22nd Street, Houston, Texas 77008',
      phone: {
        label: '(713) 863-9835',
        href: 'tel:+17138639835',
      },
    },
    {
      title: 'Experience Friendr as an adopter',
      body: 'See exactly what your adopters will experience.',
      link: {
        label: 'Take the matching quiz',
        href: ROUTES.adoptShelterQuizRoot + '/friends-for-life',
      },
    },
    {
      title: 'Grant eligibility',
      body: 'We offer 5 Standard-tier grants annually to qualifying nonprofit shelters.',
      link: {
        label: 'Contact us to find out if you qualify.',
        href: 'mailto:friendr@friends4life.org',
      },
    },
  ] satisfies LandingContactInfoBlock[],
};

export const landingFooterColumns: LandingFooterColumn[] = [
  {
    title: 'Solutions',
    links: [
      { label: 'For Shelters', href: `#${LANDING_SECTION_IDS.features}` },
      { label: 'For Rescues', href: `#${LANDING_SECTION_IDS.features}` },
      { label: 'For Fosters', href: `#${LANDING_SECTION_IDS.features}` },
      { label: 'For Adopters', href: ROUTES.adoptShelterQuizRoot },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', href: `#${LANDING_SECTION_IDS.hero}` },
      { label: 'Careers', href: `#${LANDING_SECTION_IDS.contact}` },
      { label: 'Press', href: `#${LANDING_SECTION_IDS.contact}` },
      { label: 'Contact', href: `#${LANDING_SECTION_IDS.contact}` },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '#' },
      { label: 'Terms of Service', href: ROUTES.adminSettingsTerms },
      { label: 'Cookie Policy', href: '#' },
    ],
  },
];

export const landingMobileNavLabel = 'Menu';
